/**
 * Pure helpers for reading the org's `userSettingsPolicy` block out of
 * `OpenclawConfig.org`. The runtime is authoritative — it rejects bad
 * patches with 409 `USER_SETTING_DISABLED_BY_ORG` and carries
 * `disabledKeys[]` back — but the UI mirrors that decision client-side
 * so we can disable the affected controls + show the "Disabled by your
 * organization administrator" tooltip BEFORE the user clicks Save.
 *
 * Defaults follow the canonical guide §1.1 / §2: an unspecified key
 * means "user can edit" (true). Only an explicit `false` locks.
 */

import type {
  ChannelId,
  OrgOpenclawConfig,
  UserSettingsPolicy,
} from "@/api/lx-openclaw-config";

/** Re-export the canonical `ChannelId` from the API client so callers
 *  that import the helper alongside the type get one consistent symbol
 *  (the union lives in `api/lx-openclaw-config.ts` as the single source
 *  of truth). */
export type { ChannelId };

/** Top-level user-editable keys this UI surfaces. Mirror of the
 *  canonical guide §1.1 Tier-B-user column.
 *
 *  `agentIdentity` was REMOVED 2026-07-27 by operator ruling — the assistant's
 *  name and avatar are the user's discretion and an org may not lock them. The
 *  rest of the mechanism is untouched and still governs voice / browser / push /
 *  personaSuffix / channels. */
export type UserPolicyKey =
  | "personaSuffix"
  | "locale"
  | "push"
  | "browser"
  | "voice";

/**
 * Resolve whether a top-level user-editable key is disabled by the
 * org. Default = allowed (`false`) when the policy is absent or the
 * key is unspecified — matches the runtime's `findPolicyDisabledKeys`
 * default behaviour.
 */
export function isDisabledByOrg(
  org: OrgOpenclawConfig | null | undefined,
  key: UserPolicyKey,
): boolean {
  const policy = org?.userSettingsPolicy;
  if (!policy) return false;
  return policy[key] === false;
}

/**
 * Per-channel granular kill-switch — `userSettingsPolicy.channels.<id>`.
 * Default = allowed when unspecified.
 */
export function isChannelDisabledByOrg(
  org: OrgOpenclawConfig | null | undefined,
  channel: ChannelId,
): boolean {
  const channels = org?.userSettingsPolicy?.channels;
  if (!channels) return false;
  return channels[channel] === false;
}

/**
 * Render-time helper: the list of policy keys the org has locked.
 * Useful for surfacing a single "Some settings are locked by your
 * organization administrator" banner above the tab content.
 */
export function listDisabledKeys(
  org: OrgOpenclawConfig | null | undefined,
): string[] {
  const policy: UserSettingsPolicy | undefined = org?.userSettingsPolicy;
  if (!policy) return [];
  const out: string[] = [];
  const topKeys: UserPolicyKey[] = [
    "personaSuffix",
    "locale",
    "push",
    "browser",
    "voice",
  ];
  for (const k of topKeys) {
    if (policy[k] === false) out.push(k);
  }
  const ch = policy.channels ?? {};
  for (const c of ["whatsapp", "telegram", "email"] as const) {
    if (ch[c] === false) out.push(`channels.${c}`);
  }
  return out;
}

/**
 * Two-endpoint verdict for a channel (`ai-docs/lexiun-user-setup.md` §2-bis).
 *
 * TRI-STATE on purpose. A boolean forced "cannot tell" to collapse into one of
 * the two real answers, and the safe-looking collapse is the wrong one: while
 * the gateway is down or reconnecting, `channelComplete` is absent, and
 * answering "not half-open" made the badge render green "Conectado" — the exact
 * presentation §2-bis forbids for a channel that may be live and ownerless.
 * Observed during verification, not theorised.
 *
 *  - `complete`  — both endpoints closed; the channel is genuinely the lawyer's.
 *  - `half-open` — its own door is up, the command door is not: it answers
 *                  strangers while nobody, the lawyer included, can command it.
 *  - `unknown`   — no evidence either way. Never rendered as success.
 *
 * The `unknown` window is NOT just boot: a live WhatsApp socket 408s and
 * auto-reconnects on normal Baileys backoff (observed on galvão, twice in an
 * hour), and each reconnect is a real window where `setup.status` is mid-resolve
 * and `channelComplete` is absent. So this branch is load-bearing on a running
 * channel, which is exactly why a boolean that collapsed it into "fine" briefly
 * rendered the §2-bis-forbidden green on a possibly-ownerless channel.
 *
 * Reads `channelComplete` and NOTHING else. Inferring from the channel-agnostic
 * `im-owner-pairing` step would reintroduce the cross-matching bug the runtime
 * lane fixed (a Telegram owner satisfying a WhatsApp door).
 */
export type ChannelEndpointVerdict = "complete" | "half-open" | "unknown";

export function channelEndpointVerdict(
  id: string,
  connected: boolean,
  channelComplete: Partial<Record<string, boolean>> | undefined,
): ChannelEndpointVerdict {
  // Only a connected channel can be half-open; a disconnected one is simply off.
  if (!connected) return "complete";
  // Channels with no command-door concept (email) are never half-open.
  if (id !== "whatsapp" && id !== "telegram") return "complete";
  if (channelComplete === undefined) return "unknown";
  const v = channelComplete[id];
  if (v === true) return "complete";
  if (v === false) return "half-open";
  return "unknown";
}
